import React from 'react';
import { Card, CardBody, Table, Badge } from 'reactstrap';
import { toast } from 'react-toastify';

import ActionButton from '../../../../../Components/Common/ActionButton';
import { formatDateTime } from '../../formatters';
import { DOC_SOURCE_BADGE } from '../invitationExternalConstants';
import * as api from '../invitationExternalApi';

// Invitation - External > Supporting Document tab — VIEW ONLY.
//
// Documents uploaded during Planning and Procurement, filtered to
// showToDmut = true — the DMUT must not see the buyer's internal attachments.
// The filter is applied here as well as in the data layer: it is an access rule,
// not a presentation choice, so it should not depend on the caller remembering.
//
// Preview and Download both need the file bytes, which do not exist while the
// module is dummy-backed (TODO(API)) — the api layer rejects with an explanatory
// message and both actions surface it rather than appearing to succeed.
const SupportingDocumentTab = ({ invitationId, detail }) => {
  const docs = (detail?.supportingDocuments || []).filter(d => d.showToDmut);

  const handlePreview = async doc => {
    try {
      const blob = await api.downloadDocument(invitationId, doc.id);
      // Live path: open the returned blob in a new tab for inline preview.
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.info(`Preview unavailable: ${error}`);
    }
  };

  const handleDownload = async doc => {
    try {
      const blob = await api.downloadDocument(invitationId, doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName || doc.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.info(`Download unavailable: ${error}`);
    }
  };

  return (
    <div>
      <div className='mb-3'>
        <h5 className='mb-1'>
          <i className='ri-file-text-line me-2'></i>
          Supporting Documents
        </h5>
        <p className='text-muted mb-0 fs-12'>
          Documents shared with Registered Supplier from the Planning and
          Procurement stages.
        </p>
      </div>

      <Card className='border shadow-none'>
        <CardBody className='p-0'>
          <div className='table-responsive'>
            <Table className='table-sm align-middle mb-0'>
              <thead className='table-light'>
                <tr>
                  <th>Document Name</th>
                  <th>Source</th>
                  <th>Last Updated</th>
                  <th className='text-center'>Action</th>
                </tr>
              </thead>
              <tbody>
                {docs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className='text-center text-muted py-3'>
                      No documents
                    </td>
                  </tr>
                ) : (
                  docs.map(d => (
                    <tr key={d.id}>
                      <td>
                        <i className='ri-file-2-line text-muted me-2'></i>
                        {d.name}
                      </td>
                      <td>
                        <Badge
                          color={DOC_SOURCE_BADGE[d.source] || 'secondary'}
                          className='px-2'
                        >
                          {d.source}
                        </Badge>
                      </td>
                      <td>{formatDateTime(d.lastUpdated)}</td>
                      <td className='text-center'>
                        <div className='d-flex gap-1 justify-content-center'>
                          <ActionButton
                            variant='secondary'
                            size='sm'
                            icon='ri-eye-line'
                            title='Preview'
                            onClick={() => handlePreview(d)}
                          >
                            Preview
                          </ActionButton>
                          <ActionButton
                            variant='secondary'
                            size='sm'
                            icon='ri-download-2-line'
                            title='Download'
                            onClick={() => handleDownload(d)}
                          >
                            Download
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default SupportingDocumentTab;
